import { createContext, useContext, useReducer, useEffect, useCallback, type ReactNode } from 'react';
import type { QuizBank, AppSettings, HQBFile } from '../types';
import { DEFAULT_SETTINGS } from '../types';
import { generateBankId, saveToStorage, loadFromStorage, removeFromStorage, saveCookie, loadCookie } from '../utils';

interface AppState {
  banks: QuizBank[];
  settings: AppSettings;
  agreedDisclaimer: boolean;
}

type AppAction =
  | { type: 'ADD_BANK'; payload: { hqb: HQBFile; source: 'builtin' | 'user' } }
  | { type: 'REMOVE_BANK'; payload: string }
  | { type: 'UPDATE_SETTINGS'; payload: Partial<AppSettings> }
  | { type: 'LOAD_STATE'; payload: AppState }
  | { type: 'AGREE_DISCLAIMER' }
  | { type: 'REVOKE_DISCLAIMER' }
  | { type: 'RESET' };

const initialState: AppState = {
  banks: [],
  settings: DEFAULT_SETTINGS,
  agreedDisclaimer: typeof window !== 'undefined' ? (loadFromStorage<boolean>('agreedDisclaimer', false)) : false,
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'ADD_BANK': {
      const { hqb, source } = action.payload;
      // Prevent duplicates by name
      if (state.banks.some(b => b.name === hqb.metadata.title)) return state;
      const newBank: QuizBank = {
        id: generateBankId(hqb.metadata.title),
        name: hqb.metadata.title,
        source,
        addedAt: new Date().toISOString(),
        hqb,
      };
      return { ...state, banks: [...state.banks, newBank] };
    }
    case 'REMOVE_BANK':
      return { ...state, banks: state.banks.filter(b => b.id !== action.payload) };
    case 'UPDATE_SETTINGS':
      return { ...state, settings: { ...state.settings, ...action.payload } };
    case 'LOAD_STATE':
      return action.payload;
    case 'AGREE_DISCLAIMER':
      return { ...state, agreedDisclaimer: true };
    case 'REVOKE_DISCLAIMER':
      return { ...state, agreedDisclaimer: false };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

interface AppContextType {
  state: AppState;
  addBank: (hqb: HQBFile, source: 'builtin' | 'user') => void;
  removeBank: (id: string) => void;
  updateSettings: (settings: Partial<AppSettings>) => void;
  getBankById: (id: string) => QuizBank | undefined;
  reloadBuiltinBanks: () => Promise<void>;
  agreeDisclaimer: () => void;
  revokeDisclaimer: () => void;
}

const AppContext = createContext<AppContextType | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Load settings from cookie, banks from localStorage
  useEffect(() => {
    const savedBanks = loadFromStorage<QuizBank[]>('banks', []);
    const savedSettings: AppSettings = (() => {
      const raw = loadCookie('settings');
      if (!raw) return DEFAULT_SETTINGS;
      try { return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) }; } catch { return DEFAULT_SETTINGS; }
    })();
    dispatch({
      type: 'LOAD_STATE',
      payload: { banks: savedBanks, settings: savedSettings, agreedDisclaimer: state.agreedDisclaimer },
    });
  }, []);

  // Load builtin banks
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const resp = await fetch('/builtin-banks/index.json');
        if (!resp.ok || cancelled) return;
        const fileList: string[] = await resp.json();
        for (const file of fileList) {
          if (cancelled) return;
          try {
            const fileResp = await fetch(`/builtin-banks/${encodeURIComponent(file)}`);
            if (!fileResp.ok) continue;
            const hqb: HQBFile = await fileResp.json();
            dispatch({ type: 'ADD_BANK', payload: { hqb, source: 'builtin' } });
          } catch { /* skip */ }
        }
      } catch { /* builtin not available */ }
    }
    load();
  }, []);

  // Persist banks to localStorage, settings to cookies
  useEffect(() => {
    saveToStorage('banks', state.banks);
  }, [state.banks]);
  useEffect(() => {
    saveCookie('settings', JSON.stringify(state.settings));
  }, [state.settings]);
  useEffect(() => {
    saveToStorage('agreedDisclaimer', state.agreedDisclaimer);
  }, [state.agreedDisclaimer]);

  const addBank = useCallback((hqb: HQBFile, source: 'builtin' | 'user') => {
    dispatch({ type: 'ADD_BANK', payload: { hqb, source } });
  }, []);

  const removeBank = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_BANK', payload: id });
  }, []);

  const updateSettings = useCallback((settings: Partial<AppSettings>) => {
    dispatch({ type: 'UPDATE_SETTINGS', payload: settings });
  }, []);

  const getBankById = useCallback(
    (id: string) => state.banks.find(b => b.id === id),
    [state.banks]
  );

  const reloadBuiltinBanks = useCallback(async () => {
    const userBanks = state.banks.filter(b => b.source === 'user');
    removeFromStorage('banks');
    dispatch({ type: 'LOAD_STATE', payload: { banks: userBanks, settings: state.settings, agreedDisclaimer: state.agreedDisclaimer } });
    try {
      const resp = await fetch('/builtin-banks/index.json');
      if (!resp.ok) return;
      const fileList: string[] = await resp.json();
      for (const file of fileList) {
        const fileResp = await fetch(`/builtin-banks/${encodeURIComponent(file)}`);
        if (!fileResp.ok) continue;
        const hqb: HQBFile = await fileResp.json();
        dispatch({ type: 'ADD_BANK', payload: { hqb, source: 'builtin' } });
      }
    } catch { /* skip */ }
  }, [state.banks, state.settings, state.agreedDisclaimer]);

  const agreeDisclaimer = useCallback(() => {
    dispatch({ type: 'AGREE_DISCLAIMER' });
  }, []);

  const revokeDisclaimer = useCallback(() => {
    dispatch({ type: 'REVOKE_DISCLAIMER' });
  }, []);

  return (
    <AppContext.Provider value={{ state, addBank, removeBank, updateSettings, getBankById, reloadBuiltinBanks, agreeDisclaimer, revokeDisclaimer }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within an AppProvider');
  return ctx;
}
